import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { coursesApi, videosApi } from '@/lib/api';
import { formatPrice } from '@/lib/utils';
import { useAuth } from '@/stores/auth';
import VideoJsPlayer from '@/components/VideoJsPlayer';

function Ic({ type, size = 20, color = 'var(--soleil-gold)' }: { type: string; size?: number; color?: string }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: '2', strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  const icons: Record<string, React.ReactNode> = {
    clock: <svg {...p}><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    book: <svg {...p}><path d="M4 19.5A2.5 2.5 0 016.5 17H20" /><path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z" /></svg>,
    users: <svg {...p}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87" /><path d="M16 3.13a4 4 0 010 7.75" /></svg>,
    check: <svg {...p}><polyline points="20 6 9 17 4 12" /></svg>,
    play: <svg {...p}><circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" fill={color} stroke="none" /></svg>,
    lock: <svg {...p}><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>,
    down: <svg {...p}><polyline points="6 9 12 15 18 9" /></svg>,
    right: <svg {...p}><polyline points="9 18 15 12 9 6" /></svg>,
    arrow: <svg {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
  };
  return <>{icons[type] || null}</>;
}

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function SoleilCourseDetailPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isRTL = i18n.language === 'ar';
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [previewVideo, setPreviewVideo] = useState<{ lessonId: string; title: string; url: string; signingParams?: Record<string, string> } | null>(null);
  const [loadingVideo, setLoadingVideo] = useState<string | null>(null);

  const { data: course, isLoading } = useQuery({
    queryKey: ['course', slug],
    queryFn: () => coursesApi.get(slug!),
    enabled: !!slug,
  });

  const toggleModule = (moduleId: string) => {
    const next = new Set(expandedModules);
    if (next.has(moduleId)) next.delete(moduleId); else next.add(moduleId);
    setExpandedModules(next);
  };

  const handlePlayFree = async (lessonId: string, title: string) => {
    try {
      setLoadingVideo(lessonId);
      const response = await videosApi.getFreeStreamUrl(lessonId);
      const data = (response as any)?.data || response;
      setPreviewVideo({ lessonId, title, url: data.playlistUrl?.url || data.playlistUrl, signingParams: data.signingParams });
    } catch (error) {
      console.error('Failed to load free video:', error);
    } finally {
      setLoadingVideo(null);
    }
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontFamily: S.font, color: 'var(--soleil-mut)' }}>Loading...</span></div>;
  if (!course) return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontFamily: S.font, color: 'var(--soleil-mut)' }}>Course not found</span></div>;

  const modules = course.modules || [];
  const totalLessons = modules.reduce((acc: number, m: any) => acc + (m.lessons?.length || 0), 0);

  return (
    <div style={{ background: 'var(--soleil-cream)', padding: '48px 24px 72px' }}>
      {/* Video Preview Modal */}
      {previewVideo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ width: '100%', maxWidth: 900 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ fontFamily: S.font, fontSize: 16, fontWeight: 600, color: '#fff' }}>{previewVideo.title}</h3>
              <button onClick={() => setPreviewVideo(null)} style={{ color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <div style={{ borderRadius: 12, overflow: 'hidden' }}>
              <VideoJsPlayer videoUrl={previewVideo.url} signingParams={previewVideo.signingParams} onEnded={() => setPreviewVideo(null)} className="w-full" />
            </div>
          </div>
        </div>
      )}

      <div className="container" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className="soleil-grid-detail" style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 36, alignItems: 'start' }}>
          {/* Left — course info */}
          <div>
            <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.15em', marginBottom: 12 }}>
              {isRTL ? 'تفاصيل الدورة' : 'COURSE DETAILS'}
            </div>
            <h1 style={{ fontFamily: S.heading, fontSize: 'clamp(28px, 3.5vw, 40px)', fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 16 }}>
              {isRTL ? course.titleAr : course.titleEn}
            </h1>
            <p style={{ fontFamily: S.font, fontSize: 15, color: 'var(--soleil-sub)', lineHeight: 1.75, marginBottom: 24 }}>
              {isRTL ? course.shortDescriptionAr : course.shortDescriptionEn}
            </p>

            <div style={{ display: 'flex', gap: 20, marginBottom: 36, flexWrap: 'wrap' }}>
              {[
                ['clock', `${course.durationHours || 0} ${isRTL ? 'ساعة' : 'hours'}`],
                ['book', `${modules.length} ${isRTL ? 'وحدات' : 'modules'}`],
                ['users', `${course.enrollmentCount || 0} ${isRTL ? 'طالب' : 'students'}`],
              ].map(([ic, label], i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Ic type={ic} size={16} color="var(--soleil-mut)" />
                  <span style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)' }}>{label}</span>
                </div>
              ))}
            </div>

            {/* About */}
            <div style={{ marginBottom: 40 }}>
              <h2 style={{ fontFamily: S.heading, fontSize: 24, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 12 }}>
                {isRTL ? 'عن هذه الدورة' : 'About This Course'}
              </h2>
              <p style={{ fontFamily: S.font, fontSize: 14.5, color: 'var(--soleil-sub)', lineHeight: 1.8 }}>
                {isRTL ? course.descriptionAr : course.descriptionEn}
              </p>
            </div>

            {/* Curriculum */}
            <div>
              <h2 style={{ fontFamily: S.heading, fontSize: 24, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 8 }}>
                {isRTL ? 'محتوى الدورة' : 'Course Curriculum'}
              </h2>
              <p style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-mut)', marginBottom: 20 }}>
                {modules.length} {isRTL ? 'وحدات' : 'modules'} • {totalLessons} {isRTL ? 'درس' : 'lessons'}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {modules.map((module: any) => (
                  <div key={module.id} style={{ background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 14, overflow: 'hidden' }}>
                    <button
                      onClick={() => toggleModule(module.id)}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Ic type={expandedModules.has(module.id) ? 'down' : 'right'} size={18} color="var(--soleil-mut)" />
                        <span style={{ fontFamily: S.font, fontSize: 14, fontWeight: 600, color: 'var(--soleil-ink)' }}>
                          {isRTL ? module.titleAr : module.titleEn}
                        </span>
                        <span style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-mut)', marginLeft: 8 }}>
                          {module.lessons?.length || 0} {isRTL ? 'دروس' : 'lessons'}
                        </span>
                      </div>
                    </button>

                    {expandedModules.has(module.id) && module.lessons && (
                      <div style={{ borderTop: '1px solid var(--soleil-sand)' }}>
                        {module.lessons.map((lesson: any) => (
                          <div
                            key={lesson.id}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '12px 20px', borderBottom: '1px solid var(--soleil-sand)',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {lesson.isFree ? (
                                <button
                                  onClick={() => handlePlayFree(lesson.id, isRTL ? lesson.titleAr : lesson.titleEn)}
                                  disabled={loadingVideo === lesson.id}
                                  style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--soleil-gold-dark)' }}
                                >
                                  <Ic type="play" size={16} color="var(--soleil-gold)" />
                                  <span style={{ fontFamily: S.font, fontSize: 13, fontWeight: 500 }}>
                                    {isRTL ? lesson.titleAr : lesson.titleEn}
                                  </span>
                                </button>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--soleil-mut)' }}>
                                  <Ic type="lock" size={14} color="var(--soleil-mut)" />
                                  <span style={{ fontFamily: S.font, fontSize: 13 }}>
                                    {isRTL ? lesson.titleAr : lesson.titleEn}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {lesson.isFree && (
                                <span style={{
                                  fontFamily: S.font, fontSize: 10, fontWeight: 700,
                                  background: 'var(--soleil-olive-pale)', color: 'var(--soleil-olive)',
                                  padding: '3px 10px', borderRadius: 100,
                                }}>
                                  {isRTL ? 'مجاني' : 'Free'}
                                </span>
                              )}
                              {lesson.videoDuration && (
                                <span style={{ fontFamily: S.font, fontSize: 12, color: 'var(--soleil-mut)' }}>
                                  {formatDuration(lesson.videoDuration)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — pricing card */}
          <div className="soleil-detail-sidebar" style={{ position: 'sticky', top: 88 }}>
            <div style={{ background: '#fff', border: '1px solid var(--soleil-sand)', borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 32px rgba(46,42,38,0.06)' }}>
              <div style={{ position: 'relative', height: 180, background: 'linear-gradient(135deg, var(--soleil-dark), var(--soleil-d2))', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                {course.thumbnailUrl ? (
                  <img src={course.thumbnailUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
                ) : (
                  <Ic type="play" size={48} color="rgba(250,248,244,0.5)" />
                )}
              </div>
              <div style={{ padding: '28px 24px' }}>
                <div style={{ fontFamily: S.heading, fontSize: 32, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 20 }}>
                  {formatPrice(course.price, course.currency)}
                </div>
                <Link
                  to={isAuthenticated ? `/checkout/${course.id}` : '/login'}
                  style={{
                    display: 'block', width: '100%', padding: 15, textAlign: 'center',
                    fontFamily: S.font, fontSize: 15, fontWeight: 600, color: 'var(--soleil-dark)',
                    background: 'linear-gradient(135deg, var(--soleil-gold), var(--soleil-gold-light))',
                    border: 'none', borderRadius: 11, textDecoration: 'none',
                    boxShadow: '0 4px 16px rgba(184,148,58,0.25)', marginBottom: 20,
                    boxSizing: 'border-box',
                  }}
                >
                  {t('courses.enroll') || (isRTL ? 'سجّل الآن' : 'Enroll Now')}
                </Link>
                {[
                  isRTL ? 'وصول مدى الحياة' : 'Full lifetime access',
                  isRTL ? 'شهادة إتمام' : 'Certificate of completion',
                  isRTL ? 'موارد قابلة للتحميل' : 'Downloadable resources',
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < 2 ? '1px solid var(--soleil-sand)' : 'none' }}>
                    <Ic type="check" size={15} color="var(--soleil-olive)" />
                    <span style={{ fontFamily: S.font, fontSize: 13, color: 'var(--soleil-sub)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
